import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import {
  Alert,
  Badge,
  Button,
  Empty,
  LinkButton,
  Loading,
  PageHeader,
  Tabs,
  TextInput,
} from "../../components/ui";
import { API_URL, api, errorMessage, qs } from "../../lib/api";
import { dateTimeBR } from "../../lib/format";
import type { Page } from "../../lib/types";

type Tab = "audit" | "webhooks" | "jobs" | "ai" | "emails";

interface AuditRow {
  id: string;
  actorId: string | null;
  action: string;
  targetType: string;
  targetId: string | null;
  before: unknown;
  after: unknown;
  reason: string | null;
  ip: string | null;
  createdAt: string;
}

export function AdminLogs() {
  const [tab, setTab] = useState<Tab>("audit");
  return (
    <>
      <PageHeader
        title="Logs"
        subtitle="Auditoria, eventos da Asaas, jobs com falha, sessões de IA marcadas e e-mails com erro."
      />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: "audit", label: "Auditoria" },
          { value: "webhooks", label: "Eventos da Asaas" },
          { value: "jobs", label: "Jobs com falha" },
          { value: "ai", label: "Sessões marcadas" },
          { value: "emails", label: "E-mails com erro" },
        ]}
      />
      {tab === "audit" ? <AuditTab /> : null}
      {tab === "webhooks" ? <WebhooksTab /> : null}
      {tab === "jobs" ? <JobsTab /> : null}
      {tab === "ai" ? <FlagsTab /> : null}
      {tab === "emails" ? <EmailsTab /> : null}
    </>
  );
}

function AuditTab() {
  const [f, setF] = useState({ action: "", actorId: "", targetId: "", from: "", to: "" });
  const [applied, setApplied] = useState(f);
  const params = {
    action: applied.action || undefined,
    actorId: applied.actorId || undefined,
    targetId: applied.targetId || undefined,
    from: applied.from || undefined,
    to: applied.to || undefined,
  };
  const q = useInfiniteQuery({
    queryKey: ["audit", applied],
    queryFn: ({ pageParam }) =>
      api.get<Page<AuditRow>>(
        `/v1/admin/audit-logs${qs({ ...params, cursor: pageParam ?? undefined, limit: 50 })}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (l) => l.nextCursor,
  });
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="stack">
      <form
        className="grid cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied(f);
        }}
      >
        <TextInput
          label="Ação"
          placeholder="enrollment.refunded"
          value={f.action}
          onChange={(e) => setF({ ...f, action: e.target.value })}
        />
        <TextInput
          label="Pessoa (ID)"
          value={f.actorId}
          onChange={(e) => setF({ ...f, actorId: e.target.value })}
        />
        <TextInput
          label="De"
          type="date"
          value={f.from}
          onChange={(e) => setF({ ...f, from: e.target.value })}
        />
        <TextInput
          label="Até"
          type="date"
          value={f.to}
          onChange={(e) => setF({ ...f, to: e.target.value })}
        />
        <div className="row">
          <Button type="submit" variant="secondary">
            Filtrar
          </Button>
          <LinkButton
            variant="ghost"
            href={`${API_URL}/v1/admin/audit-logs${qs({ ...params, format: "csv" })}`}
          >
            Exportar CSV
          </LinkButton>
        </div>
      </form>
      {q.isLoading ? <Loading /> : null}
      {q.error ? <Alert kind="risk">{errorMessage(q.error)}</Alert> : null}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Quando</th>
              <th>Ação</th>
              <th>Alvo</th>
              <th>Motivo</th>
              <th>Mudança</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="small">{dateTimeBR(r.createdAt)}</td>
                <td>
                  <code className="small">{r.action}</code>
                </td>
                <td className="small">
                  {r.targetType} {r.targetId ? <code>{r.targetId.slice(0, 8)}</code> : null}
                </td>
                <td className="small">{r.reason}</td>
                <td className="small" style={{ maxWidth: 320, wordBreak: "break-word" }}>
                  {r.after ? JSON.stringify(r.after).slice(0, 200) : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {q.hasNextPage ? (
        <Button
          variant="secondary"
          loading={q.isFetchingNextPage}
          onClick={() => q.fetchNextPage()}
        >
          Carregar mais
        </Button>
      ) : null}
    </div>
  );
}

function WebhooksTab() {
  const [status, setStatus] = useState("");
  const q = useQuery({
    queryKey: ["webhooks", status],
    queryFn: () =>
      api.get<{
        items: {
          id: string;
          asaasEventId: string;
          event: string;
          status: string;
          error: string | null;
          receivedAt: string;
          processedAt: string | null;
        }[];
      }>(`/v1/admin/webhook-events${qs({ status: status || undefined })}`),
  });
  const color = (s: string) =>
    s === "processed" ? "green" : s === "failed" ? "red" : s === "ignored" ? "gray" : "blue";
  return (
    <div className="stack">
      <div className="chips">
        {["", "received", "processed", "ignored", "failed"].map((s) => (
          <button
            key={s}
            type="button"
            className={`chip${status === s ? " active" : ""}`}
            onClick={() => setStatus(s)}
          >
            {s || "todos"}
          </button>
        ))}
      </div>
      {q.isLoading ? <Loading /> : null}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Recebido</th>
              <th>Evento</th>
              <th>ID</th>
              <th>Status</th>
              <th>Erro</th>
            </tr>
          </thead>
          <tbody>
            {q.data?.items.map((w) => (
              <tr key={w.id}>
                <td className="small">{dateTimeBR(w.receivedAt)}</td>
                <td>{w.event}</td>
                <td className="small mono">{w.asaasEventId.slice(0, 24)}</td>
                <td>
                  <Badge color={color(w.status)}>{w.status}</Badge>
                </td>
                <td className="small">{w.error}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function JobsTab() {
  const q = useQuery({
    queryKey: ["jobs-failed"],
    queryFn: () =>
      api.get<{
        items: {
          id: string;
          name: string;
          state: string;
          retry_count: number;
          created_on: string;
          output: unknown;
        }[];
      }>("/v1/admin/jobs?state=failed"),
  });
  if (q.isLoading) return <Loading />;
  if (!q.data?.items.length) return <Empty>Nenhum job com falha.</Empty>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Criado</th>
            <th>Job</th>
            <th className="num">Tentativas</th>
            <th>Saída</th>
          </tr>
        </thead>
        <tbody>
          {q.data.items.map((j) => (
            <tr key={j.id}>
              <td className="small">{dateTimeBR(j.created_on)}</td>
              <td>{j.name}</td>
              <td className="num">{j.retry_count}</td>
              <td className="small">{JSON.stringify(j.output)?.slice(0, 200)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FlagsTab() {
  const q = useQuery({
    queryKey: ["ai-flags"],
    queryFn: () =>
      api.get<{
        items: {
          id: string;
          studentId: string;
          studentName: string;
          flag: string;
          startedAt: string;
          endReason: string | null;
        }[];
      }>("/v1/admin/ai-flags"),
  });
  if (q.isLoading) return <Loading />;
  if (!q.data?.items.length) return <Empty>Nenhuma sessão marcada.</Empty>;
  return (
    <div className="stack">
      {q.data.items.map((s) => (
        <div key={s.id} className="list-item">
          <div>
            <Link to={`/admin/alunos/${s.studentId}`}>{s.studentName}</Link>
            <div className="small muted">{dateTimeBR(s.startedAt)}</div>
          </div>
          <Badge color="red">{s.flag === "wellbeing" ? "Bem-estar" : "Conteúdo sensível"}</Badge>
        </div>
      ))}
    </div>
  );
}

function EmailsTab() {
  const q = useQuery({
    queryKey: ["email-errors"],
    queryFn: () =>
      api.get<{
        items: {
          id: string;
          type: string;
          to: string;
          status: string;
          error: string | null;
          attempts: number;
          createdAt: string;
        }[];
      }>("/v1/admin/email-deliveries?days=7"),
  });
  if (q.isLoading) return <Loading />;
  if (!q.data?.items.length) return <Empty>Nenhum e-mail com erro nos últimos 7 dias.</Empty>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Quando</th>
            <th>Tipo</th>
            <th>Para</th>
            <th>Status</th>
            <th>Erro</th>
          </tr>
        </thead>
        <tbody>
          {q.data.items.map((e) => (
            <tr key={e.id}>
              <td className="small">{dateTimeBR(e.createdAt)}</td>
              <td className="small">{e.type}</td>
              <td className="small">{e.to}</td>
              <td>
                <Badge color={e.status === "failed" ? "red" : "gold"}>
                  {e.status === "failed" ? "falhou" : "suprimido"}
                </Badge>
              </td>
              <td className="small">{e.error}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
